import React, { useEffect, useState } from "react";
import {
  db, ACTIVITY_TYPES, TERMS, getTerm, getCurrentTermId, getSubmissionTermId, calcTermScores
} from "./firebase";
import {
  collection, query, orderBy, onSnapshot, doc, updateDoc
} from "firebase/firestore";

export default function AdminView() {
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState({ period: "all", status: "all", student: "" });
  const [editing, setEditing] = useState({});
  const currentTermId = getCurrentTermId();
  const [termId, setTermId] = useState(currentTermId);
  const term = getTerm(termId);

  const changeTerm = (id) => {
    setTermId(id);
    setFilter(f => ({ ...f, period: "all" }));
  };

  useEffect(() => {
    const q = query(collection(db, "submissions"), orderBy("submittedAt", "desc"));
    return onSnapshot(q, (snap) => {
      setSubmissions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  const handleApprove = async (sub) => {
    const finalPoints = editing[sub.id]?.finalPoints ?? sub.finalPoints ?? sub.provisionalPoints ?? "";
    if (finalPoints === "" || isNaN(Number(finalPoints))) {
      window.alert("確定点数を入力してください。");
      return;
    }
    const teacherNote = editing[sub.id]?.teacherNote ?? sub.teacherNote ?? "";
    await updateDoc(doc(db, "submissions", sub.id), {
      status: "approved",
      finalPoints: Number(finalPoints),
      teacherNote,
    });
    setEditing(e => { const n = { ...e }; delete n[sub.id]; return n; });
  };

  const handleModify = async (sub) => {
    const finalPoints = editing[sub.id]?.finalPoints ?? sub.finalPoints ?? sub.provisionalPoints ?? "";
    if (finalPoints === "" || isNaN(Number(finalPoints))) {
      window.alert("確定点数を入力してください。");
      return;
    }
    const teacherNote = editing[sub.id]?.teacherNote ?? sub.teacherNote ?? "";
    await updateDoc(doc(db, "submissions", sub.id), {
      status: "modified",
      finalPoints: Number(finalPoints),
      teacherNote,
    });
    setEditing(e => { const n = { ...e }; delete n[sub.id]; return n; });
  };

  // 選択中の学期の提出
  const termSubs = submissions.filter(s => getSubmissionTermId(s) === termId);

  // 学生一覧は全学期の提出者から作り、得点は選択中の学期で集計する
  const studentMap = {};
  submissions.forEach(s => {
    if (!studentMap[s.uid]) {
      studentMap[s.uid] = { uid: s.uid, name: s.name, email: s.email, submissions: [] };
    }
    studentMap[s.uid].submissions.push(s);
  });

  const studentSummaries = Object.values(studentMap)
    .map(st => ({ ...st, ...calcTermScores(st.submissions, termId) }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ja"));

  // Filter submissions
  const filtered = termSubs.filter(s => {
    if (filter.period !== "all" && s.periodId !== Number(filter.period)) return false;
    if (filter.status !== "all" && s.status !== filter.status) return false;
    if (filter.student && !s.name?.includes(filter.student) && !s.email?.includes(filter.student)) return false;
    return true;
  });

  const pendingCount = termSubs.filter(s => s.status === "pending").length;
  const pendingByTerm = (id) =>
    submissions.filter(s => getSubmissionTermId(s) === id && s.status === "pending").length;

  // CSV書き出し（選択中の学期のみ、Excel用にBOM付き）
  const downloadCsv = (rows, filename) => {
    const esc = (v) => {
      const str = v === null || v === undefined ? "" : String(v);
      return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const csv = rows.map(r => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportScores = () => {
    const header = ["学期", "氏名", "メール", ...term.periods.map(p => p.label), "合計"];
    const rows = studentSummaries.map(st => [
      term.label, st.name, st.email, ...st.periods.map(p => p.effective), st.total,
    ]);
    downloadCsv([header, ...rows], `speaking-report_scores_term${termId}.csv`);
  };

  const exportSubmissions = () => {
    const statusLabel = { pending: "確認中", approved: "承認済", modified: "修正" };
    const header = ["学期", "期", "氏名", "メール", "種類", "活動日", "時間", "分",
      "暫定点", "確定点", "ステータス", "内容", "AIフィードバック", "教員コメント", "提出日時"];
    const rows = [...termSubs].reverse().map(s => [
      term.label, `第${s.periodId}期`, s.name, s.email, s.type, s.date, s.hours, s.minutes,
      s.provisionalPoints, s.finalPoints, statusLabel[s.status] || s.status,
      s.description, s.aiFeedback, s.teacherNote,
      s.submittedAt ? s.submittedAt.toDate().toLocaleString("ja-JP") : "",
    ]);
    downloadCsv([header, ...rows], `speaking-report_submissions_term${termId}.csv`);
  };

  return (
    <div className="admin-view">
      <div className="term-tabs">
        {TERMS.filter(t => t.id <= currentTermId).map(t => {
          const n = pendingByTerm(t.id);
          return (
            <button
              key={t.id}
              type="button"
              className={`term-tab ${termId === t.id ? "active" : ""}`}
              onClick={() => changeTerm(t.id)}
            >
              {t.label}{n > 0 && <span className="term-tab-pending">{n}</span>}
            </button>
          );
        })}
      </div>

      {/* Student Score Overview */}
      <section className="admin-section">
        <h2>👥 学生別成績一覧（{term.label}）</h2>
        <div className="csv-buttons">
          <button type="button" className="btn-csv" onClick={exportScores}>⬇ 成績集計CSV</button>
          <button type="button" className="btn-csv" onClick={exportSubmissions}>⬇ 提出データCSV</button>
        </div>
        <div className="student-table-wrap">
          <table className="student-table">
            <thead>
              <tr>
                <th>氏名</th>
                {term.periods.map(p => <th key={p.id}>{p.label}</th>)}
                <th>合計</th>
              </tr>
            </thead>
            <tbody>
              {studentSummaries.map(st => (
                <tr key={st.uid}>
                  <td>{st.name}<br /><small>{st.email}</small></td>
                  {st.periods.map(p => (
                    <td key={p.id} className={p.effective >= p.maxPoints ? "cell-complete" : ""}>
                      {p.effective}/{p.maxPoints}
                      {p.carryover > 0 && <small className="carry">(繰{p.carryover})</small>}
                    </td>
                  ))}
                  <td><strong>{st.total}/{st.maxTotal}</strong></td>
                </tr>
              ))}
              {studentSummaries.length === 0 && (
                <tr><td colSpan={term.periods.length + 2}>提出データがありません</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Submissions Review */}
      <section className="admin-section">
        <h2>
          📋 提出一覧（{term.label}）
          {pendingCount > 0 && <span className="badge-pending">{pendingCount}件 未確認</span>}
        </h2>

        {/* Filters */}
        <div className="filter-bar">
          <select value={filter.period} onChange={e => setFilter(f => ({ ...f, period: e.target.value }))}>
            <option value="all">全期間</option>
            {term.periods.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
            <option value="all">全ステータス</option>
            <option value="pending">確認中</option>
            <option value="approved">承認済</option>
            <option value="modified">修正済</option>
          </select>
          <input
            type="text"
            placeholder="学生名で絞り込み"
            value={filter.student}
            onChange={e => setFilter(f => ({ ...f, student: e.target.value }))}
          />
        </div>

        <div className="submission-list">
          {filtered.map(s => {
            const ed = editing[s.id] || {};
            const currentFinal = ed.finalPoints !== undefined ? ed.finalPoints : (s.finalPoints ?? s.provisionalPoints ?? "");
            const currentNote = ed.teacherNote !== undefined ? ed.teacherNote : (s.teacherNote ?? "");
            const typeInfo = ACTIVITY_TYPES[s.type];

            return (
              <div key={s.id} className={`submission-card admin-card status-${s.status}`}>
                <div className="sub-header">
                  <strong>{s.name}</strong>
                  <small>{s.email}</small>
                  <span className="sub-type">種類{s.type}</span>
                  <span className="sub-date">{s.date}</span>
                  <span className="sub-time">{s.hours}時間{s.minutes}分</span>
                  <span className={`sub-status ${s.status}`}>
                    {s.status === "pending" ? "確認中" : s.status === "approved" ? "承認済" : "修正"}
                  </span>
                  <span className="period-badge">第{s.periodId}期</span>
                  {s.submittedAt && (
                    <span className="sub-timestamp">🕐 {s.submittedAt.toDate().toLocaleString("ja-JP")}</span>
                  )}
                </div>

                <p className="sub-description">{s.description}</p>

                {s.aiFeedback && (
                  <div className="ai-feedback-admin">
                    <small>🤖 AIフィードバック：{s.aiFeedback}</small>
                  </div>
                )}

                <div className="admin-controls">
                  <div className="points-control">
                    <label>
                      申告：種類{s.type}・{s.hours}時間{s.minutes}分
                      {typeInfo?.pointsPer && ` → 暫定${s.provisionalPoints ?? "?"}点`}
                    </label>
                    <div className="points-input-row">
                      <label>確定点数：</label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={currentFinal}
                        onChange={e => setEditing(ed2 => ({
                          ...ed2,
                          [s.id]: { ...ed2[s.id], finalPoints: e.target.value }
                        }))}
                        className="points-input"
                      />
                      <span>点</span>
                    </div>
                  </div>

                  <div className="note-control">
                    <label>備考：</label>
                    <input
                      type="text"
                      placeholder="コメント（任意）"
                      value={currentNote}
                      onChange={e => setEditing(ed2 => ({
                        ...ed2,
                        [s.id]: { ...ed2[s.id], teacherNote: e.target.value }
                      }))}
                      className="note-input"
                    />
                  </div>

                  <div className="action-buttons">
                    <button className="btn-approve" onClick={() => handleApprove(s)}>
                      ✓ 申告どおり承認
                    </button>
                    <button className="btn-modify" onClick={() => handleModify(s)}>
                      ✎ 変更して確定
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="empty">該当する提出がありません。</p>}
        </div>
      </section>
    </div>
  );
}
